/**
 * Utilidades de validación para distribuidores
 */

// Expresiones regulares para validación
const REGEX_PATTERNS = {
  // Celular mexicano: +52 seguido de 10 dígitos, o cualquier prefijo internacional seguido de dígitos
  PHONE: /^(\+\d{1,4})\d{10,12}$/,
  // Email básico
  EMAIL: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  // Número de cuenta bancaria (10-20 dígitos)
  BANK_ACCOUNT: /^[0-9]{10,20}$/,
  // Solo letras, espacios, acentos y algunos caracteres especiales
  NAME: /^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s\-\.]+$/,
  // Letras, números, espacios y algunos caracteres especiales para nombres de empresa
  COMPANY_NAME: /^[a-zA-Z0-9áéíóúÁÉÍÓÚñÑ\s\-\.\&\,]+$/,
  // Solo letras y espacios para ciudades
  CITY: /^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]+$/
};

// Mensajes de error
const ERROR_MESSAGES = {
  REQUIRED: 'Este campo es obligatorio',
  INVALID_FORMAT: 'El formato no es válido',
  TOO_SHORT: 'Debe tener al menos {min} caracteres',
  TOO_LONG: 'No puede tener más de {max} caracteres',
  INVALID_PHONE: 'Debe ser un número válido con prefijo internacional (ej: +52XXXXXXXXXX)',
  INVALID_EMAIL: 'Debe ser un email válido',
  INVALID_BANK_ACCOUNT: 'Debe tener entre 10 y 20 dígitos',
  INVALID_NAME: 'Solo se permiten letras, espacios y acentos',
  INVALID_COMPANY_NAME: 'Solo se permiten letras, números, espacios y algunos caracteres especiales',
  INVALID_CITY: 'Solo se permiten letras, espacios y acentos',
  DUPLICATE_EMAIL: 'Este email ya está registrado',
  DUPLICATE_PHONE: 'Este número de celular ya está registrado'
};

/**
 * Valida si un campo es requerido
 * @param {string} value - Valor a validar
 * @param {string} fieldName - Nombre del campo para el mensaje de error
 * @returns {Object} Resultado de validación
 */
export const validateRequired = (value, fieldName = 'Campo') => {
  const trimmedValue = typeof value === 'string' ? value.trim() : value;
  const isValid = trimmedValue !== null && trimmedValue !== undefined && trimmedValue !== '';
  
  return {
    isValid,
    error: isValid ? null : `${fieldName} es obligatorio`
  };
};

/**
 * Valida la longitud de un campo
 * @param {string} value - Valor a validar
 * @param {number} min - Longitud mínima
 * @param {number} max - Longitud máxima
 * @param {string} fieldName - Nombre del campo
 * @returns {Object} Resultado de validación
 */
export const validateLength = (value, min = 0, max = Infinity, fieldName = 'Campo') => {
  if (!value) return { isValid: true, error: null };
  
  const length = value.trim().length;
  
  if (length < min) {
    return {
      isValid: false,
      error: `${fieldName} debe tener al menos ${min} caracteres`
    };
  }
  
  if (length > max) {
    return {
      isValid: false,
      error: `${fieldName} no puede tener más de ${max} caracteres`
    };
  }
  
  return { isValid: true, error: null };
};

/**
 * Valida el nombre de la empresa
 * @param {string} value - Valor a validar
 * @returns {Object} Resultado de validación
 */
export const validateCompanyName = (value) => {
  // Validar requerido
  const requiredValidation = validateRequired(value, 'Nombre de empresa');
  if (!requiredValidation.isValid) {
    return requiredValidation;
  }
  
  // Validar longitud
  const lengthValidation = validateLength(value, 2, 100, 'Nombre de empresa');
  if (!lengthValidation.isValid) {
    return lengthValidation;
  }
  
  // Validar formato
  if (!REGEX_PATTERNS.COMPANY_NAME.test(value.trim())) {
    return {
      isValid: false,
      error: 'El nombre de empresa solo puede contener letras, números, espacios y algunos caracteres especiales'
    };
  }
  
  return { isValid: true, error: null };
};

/**
 * Valida el nombre del distribuidor
 * @param {string} value - Valor a validar
 * @returns {Object} Resultado de validación
 */
export const validateDistributorName = (value) => {
  // Validar requerido
  const requiredValidation = validateRequired(value, 'Nombre del distribuidor');
  if (!requiredValidation.isValid) {
    return requiredValidation;
  }
  
  // Validar longitud
  const lengthValidation = validateLength(value, 2, 50, 'Nombre del distribuidor');
  if (!lengthValidation.isValid) {
    return lengthValidation;
  }
  
  // Validar formato
  if (!REGEX_PATTERNS.NAME.test(value.trim())) {
    return {
      isValid: false,
      error: 'El nombre solo puede contener letras, espacios y acentos'
    };
  }
  
  return { isValid: true, error: null };
};

/**
 * Valida el número de celular
 * @param {string} value - Valor a validar
 * @returns {Object} Resultado de validación
 */
export const validatePhone = (value) => {
  // Validar requerido
  const requiredValidation = validateRequired(value, 'Número de celular');
  if (!requiredValidation.isValid) {
    return requiredValidation;
  }
  
  // Limpiar el valor (remover espacios y guiones)
  let cleanValue = value.replace(/[\s\-]/g, '');
  
  // Si no tiene prefijo y son 10 dígitos, agregar +52 (México) por defecto
  if (!/^\+/.test(cleanValue) && /^\d{10}$/.test(cleanValue)) {
    cleanValue = '+52' + cleanValue;
  }
  
  // Validar formato: debe tener prefijo internacional y entre 10-12 dígitos después del prefijo
  if (!REGEX_PATTERNS.PHONE.test(cleanValue)) {
    return {
      isValid: false,
      error: 'Debe ser un número válido. Ej: 2299288981 (se agrega +52) o +1 1234567890'
    };
  }
  
  return { isValid: true, error: null, cleanValue };
};

/**
 * Valida el email de cuenta eWeLink
 * @param {string} value - Valor a validar
 * @returns {Object} Resultado de validación
 */
export const validateEmail = (value) => {
  // Validar requerido
  const requiredValidation = validateRequired(value, 'Cuenta eWeLink');
  if (!requiredValidation.isValid) {
    return requiredValidation;
  }
  
  // Validar formato
  if (!REGEX_PATTERNS.EMAIL.test(value.trim())) {
    return {
      isValid: false,
      error: 'Debe ser un email válido'
    };
  }
  
  return { isValid: true, error: null };
};

/**
 * Valida el número de cuenta bancaria
 * @param {string} value - Valor a validar
 * @returns {Object} Resultado de validación
 */
export const validateBankAccount = (value) => {
  // Campo opcional
  if (!value || value.trim() === '') {
    return { isValid: true, error: null };
  }
  
  // Limpiar el valor (remover espacios y guiones)
  const cleanValue = value.replace(/[\s\-]/g, '');
  
  // Validar formato
  if (!REGEX_PATTERNS.BANK_ACCOUNT.test(cleanValue)) {
    return {
      isValid: false,
      error: 'Debe tener entre 10 y 20 dígitos'
    };
  }
  
  return { isValid: true, error: null, cleanValue };
};

/**
 * Valida la ciudad
 * @param {string} value - Valor a validar
 * @returns {Object} Resultado de validación
 */
export const validateCity = (value) => {
  // Campo opcional
  if (!value || value.trim() === '') {
    return { isValid: true, error: null };
  }
  
  // Validar longitud
  const lengthValidation = validateLength(value, 2, 50, 'Ciudad');
  if (!lengthValidation.isValid) {
    return lengthValidation;
  }
  
  // Validar formato
  if (!REGEX_PATTERNS.CITY.test(value.trim())) {
    return {
      isValid: false,
      error: 'La ciudad solo puede contener letras, espacios y acentos'
    };
  }
  
  return { isValid: true, error: null };
};

/**
 * Valida el estado/departamento
 * @param {string} value - Valor a validar
 * @returns {Object} Resultado de validación
 */
export const validateState = (value) => {
  // Campo opcional
  if (!value || value.trim() === '') {
    return { isValid: true, error: null };
  }
  
  // Validar longitud
  const lengthValidation = validateLength(value, 2, 50, 'Estado/Departamento');
  if (!lengthValidation.isValid) {
    return lengthValidation;
  }
  
  // Validar formato
  if (!REGEX_PATTERNS.CITY.test(value.trim())) {
    return {
      isValid: false,
      error: 'El estado/departamento solo puede contener letras, espacios y acentos'
    };
  }
  
  return { isValid: true, error: null };
};

/**
 * Valida todos los campos de un distribuidor
 * @param {Object} distributorData - Datos del distribuidor
 * @returns {Object} Resultado de validación completa
 */
export const validateDistributorForm = (distributorData) => {
  const errors = {};
  let isValid = true;
  
  // Validar nombre de empresa
  const companyValidation = validateCompanyName(distributorData.nombre_empresa);
  if (!companyValidation.isValid) {
    errors.nombre_empresa = companyValidation.error;
    isValid = false;
  }
  
  // Validar nombre del distribuidor
  const nameValidation = validateDistributorName(distributorData.nombre_distribuidor);
  if (!nameValidation.isValid) {
    errors.nombre_distribuidor = nameValidation.error;
    isValid = false;
  }
  
  // Validar celular
  const phoneValidation = validatePhone(distributorData.celular);
  if (!phoneValidation.isValid) {
    errors.celular = phoneValidation.error;
    isValid = false;
  }
  
  // Validar email
  const emailValidation = validateEmail(distributorData.cuenta_ewelink);
  if (!emailValidation.isValid) {
    errors.cuenta_ewelink = emailValidation.error;
    isValid = false;
  }
  
  // Validar cuenta bancaria (opcional)
  const bankValidation = validateBankAccount(distributorData.numero_cuenta_bancaria);
  if (!bankValidation.isValid) {
    errors.numero_cuenta_bancaria = bankValidation.error;
    isValid = false;
  }
  
  // Validar ciudad (opcional)
  const cityValidation = validateCity(distributorData.ciudad);
  if (!cityValidation.isValid) {
    errors.ciudad = cityValidation.error;
    isValid = false;
  }
  
  // Validar estado (opcional)
  const stateValidation = validateState(distributorData.estado);
  if (!stateValidation.isValid) {
    errors.estado = stateValidation.error;
    isValid = false;
  }
  
  return {
    isValid,
    errors,
    cleanData: {
      ...distributorData,
      celular: phoneValidation.cleanValue || distributorData.celular,
      numero_cuenta_bancaria: bankValidation.cleanValue || distributorData.numero_cuenta_bancaria
    }
  };
};

/**
 * Valida un campo individual en tiempo real
 * @param {string} fieldName - Nombre del campo
 * @param {string} value - Valor del campo
 * @returns {Object} Resultado de validación
 */
export const validateField = (fieldName, value) => {
  switch (fieldName) {
    case 'nombre_empresa':
      return validateCompanyName(value);
    case 'nombre_distribuidor':
      return validateDistributorName(value);
    case 'celular':
      return validatePhone(value);
    case 'cuenta_ewelink':
      return validateEmail(value);
    case 'numero_cuenta_bancaria':
      return validateBankAccount(value);
    case 'ciudad':
      return validateCity(value);
    case 'estado':
      return validateState(value);
    default:
      return { isValid: true, error: null };
  }
};

/**
 * Formatea un número de teléfono para mostrar
 * @param {string} phone - Número de teléfono
 * @returns {string} Número formateado
 */
export const formatPhone = (phone) => {
  if (!phone) return '';
  
  // Si ya tiene formato con +, mantenerlo
  if (phone.startsWith('+')) {
    return phone;
  }
  
  // Limpiar el número
  const clean = phone.replace(/\D/g, '');
  
  // Si empieza con 52 (México), formatear
  if (clean.startsWith('52') && clean.length === 12) {
    return `+${clean.slice(0, 2)} ${clean.slice(2, 5)} ${clean.slice(5, 8)} ${clean.slice(8)}`;
  }
  
  // Si son 10 dígitos, agregar +52 México
  if (clean.length === 10) {
    return `+52 ${clean.slice(0, 3)} ${clean.slice(3, 6)} ${clean.slice(6)}`;
  }
  
  return phone;
};

/**
 * Formatea un número de cuenta bancaria para mostrar
 * @param {string} account - Número de cuenta
 * @returns {string} Cuenta formateada
 */
export const formatBankAccount = (account) => {
  if (!account) return '';
  
  // Limpiar el número
  const clean = account.replace(/\D/g, '');
  
  // Formatear en grupos de 4 dígitos
  return clean.replace(/(\d{4})(?=\d)/g, '$1-');
};

/**
 * Obtiene sugerencias de validación para mejorar los datos
 * @param {Object} distributorData - Datos del distribuidor
 * @returns {Array} Array de sugerencias
 */
export const getValidationSuggestions = (distributorData) => {
  const suggestions = [];
  
  // Sugerir completar campos opcionales importantes
  if (!distributorData.ciudad) {
    suggestions.push({
      type: 'info',
      message: 'Considera agregar la ciudad para mejor organización'
    });
  }
  
  if (!distributorData.estado) {
    suggestions.push({
      type: 'info',
      message: 'Considera agregar el estado/departamento'
    });
  }
  
  if (!distributorData.numero_cuenta_bancaria) {
    suggestions.push({
      type: 'info',
      message: 'Agregar número de cuenta bancaria facilitará los pagos'
    });
  }
  
  // Sugerir mejoras en el formato
  if (distributorData.celular && !distributorData.celular.startsWith('+')) {
    suggestions.push({
      type: 'info',
      message: 'Se agregará automáticamente el prefijo +52 (México) si no especificas otro'
    });
  }
  
  return suggestions;
};

export default {
  validateRequired,
  validateLength,
  validateCompanyName,
  validateDistributorName,
  validatePhone,
  validateEmail,
  validateBankAccount,
  validateCity,
  validateState,
  validateDistributorForm,
  validateField,
  formatPhone,
  formatBankAccount,
  getValidationSuggestions,
  ERROR_MESSAGES,
  REGEX_PATTERNS
};